import React, { useEffect, useMemo, useState } from 'react';
import AnimatedTitle from '../AnimatedTitle';
import '../improvimania/HostScreen.css';
import '../improvimania/HostScreen/Lobby.css';
import './reaction.css';

const roundRules = {
  1: 'Round 1: Green moles only. First correct tap wins +1. Empty taps do nothing.',
  2: 'Round 2: Green moles and red bombs. Mole first hit is +1, bomb taps are -1 each.',
  3: 'Round 3: Chaos mode. Gold mole is +2, purple bomb is -2, plus standard entities.',
};

const rankScores = (rows) => [...(rows || [])].sort((a, b) => b.points - a.points);

export default function ReactionHost({
  socket,
  sessionId,
  players,
  ipAddress,
  serverType,
  onReturnToPartyPack,
  removePlayer,
  setForceRemove,
}) {
  const [state, setState] = useState({
    phase: 'lobby',
    round: 1,
    totalRounds: 3,
    roundName: 'Classic Rush',
    roundDurationSec: 30,
    briefingEndsAt: null,
    roundEndsAt: null,
    summaryEndsAt: null,
    board: [],
    feed: [],
    roundStats: null,
    avatarChoices: [],
    scoreboard: [],
  });

  const [timeLeftMs, setTimeLeftMs] = useState(0);
  const [podiumRevealCount, setPodiumRevealCount] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (!socket) return undefined;

    const onState = (incoming) => {
      setState((prev) => ({ ...prev, ...incoming }));
    };

    socket.on('reactionState', onState);
    socket.emit('reactionSyncState', { sessionId });

    return () => {
      socket.off('reactionState', onState);
    };
  }, [socket, sessionId]);

  useEffect(() => {
    const targetEndsAt = state.phase === 'round-briefing'
      ? state.briefingEndsAt
      : state.phase === 'active-round'
        ? state.roundEndsAt
        : state.phase === 'round-summary'
          ? state.summaryEndsAt
          : null;

    if (!targetEndsAt) {
      setTimeLeftMs(0);
      return undefined;
    }

    const tick = () => {
      setTimeLeftMs(Math.max(targetEndsAt - Date.now(), 0));
    };

    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [state.phase, state.briefingEndsAt, state.roundEndsAt, state.summaryEndsAt]);

  const scoreboard = useMemo(() => rankScores(state.scoreboard), [state.scoreboard]);
  const bottomUpRevealRows = useMemo(() => [...scoreboard].reverse(), [scoreboard]);
  const isProductionServer = serverType === 'fayaz.fly.dev';
  const leaderboardLocked = state.round === 3 && state.phase !== 'game-over';

  const confettiPieces = useMemo(
    () => Array.from({ length: 84 }, (_, index) => ({
      id: index,
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 1.2}s`,
      duration: `${2.6 + Math.random() * 1.8}s`,
      color: ['#ffd84d', '#69f07a', '#59b6ff', '#ff6b9e', '#c996ff'][index % 5],
      rotate: `${Math.random() * 360}deg`,
    })),
    [],
  );

  const setRoundLength = (roundDurationSec) => {
    socket.emit('reactionUpdateSettings', { sessionId, roundDurationSec: Number(roundDurationSec) });
  };

  useEffect(() => {
    if (state.phase !== 'game-over') {
      setShowConfetti(false);
      setPodiumRevealCount(0);
      return undefined;
    }

    setShowConfetti(true);
    setPodiumRevealCount(0);

    const ids = bottomUpRevealRows.map((_, index) => setTimeout(() => {
      setPodiumRevealCount(index + 1);
    }, (index + 1) * 380));

    const confettiTimeout = setTimeout(() => {
      setShowConfetti(false);
    }, 6200);

    return () => {
      ids.forEach((id) => clearTimeout(id));
      clearTimeout(confettiTimeout);
    };
  }, [state.phase, bottomUpRevealRows]);

  return (
    <div className="">
      <div className="title-bar">
        <div className="join-message">
          <>
            {isProductionServer
              ? <>
              Join at <span className="red-text">Fayaz.One</span> in your browser! Players should choose Production on their devices; no room code is needed.
              </>
              : <>
                  Join at <span className="red-text">Fayaz.One</span> in your browser!
                </>}
          </>
        </div>
        <div className="room-info">
          {!isProductionServer && <h2>Room Code: {ipAddress}</h2>}
          <h4>Session: {sessionId}</h4>
        </div>
      </div>

      {state.phase === 'lobby' ? (
        <div className="lobby-container rr-lobby-container">
          <AnimatedTitle title="Reaction Rush" />
          <div className="app-container">
            <div className="players-container">
              <h4>Players:</h4>
              {(players || []).map((player) => (
                <button
                  className="ghost-button"
                  key={player.socketId}
                  onClick={() => {
                    setForceRemove(true);
                    removePlayer(player.socketId, true);
                  }}
                >
                  <span className="rr-player-pill-avatar">{player.avatar || '😺'}</span>
                  {player.name}
                </button>
              ))}
            </div>

            <div className="settings rr-settings-panel">
              <h2 style={{ fontFamily: 'Alloy Ink' }}>Game Settings:</h2>
              <label htmlFor="rr-round-length">Round Length</label>
              <select id="rr-round-length" value={state.roundDurationSec} onChange={(e) => setRoundLength(e.target.value)}>
                <option value={15}>15 seconds</option>
                <option value={30}>30 seconds</option>
                <option value={60}>60 seconds</option>
              </select>

              <div className="rr-settings-text">
                <p>Three rounds: Classic Rush, Danger Zone, Chaos Mode.</p>
                <p>Phone shows blank holes only. TV shows moles and bombs.</p>
                <p>Need at least 2 players to begin.</p>
              </div>
            </div>
          </div>

          <button
            className="start-game primary-button"
            onClick={() => socket.emit('reactionStartGame', { sessionId })}
            disabled={(players || []).length < 2}
          >
            {(players || []).length < 2 ? 'Need at least 2 players' : 'Start Reaction Rush'}
          </button>
        </div>
      ) : (
        <div className="rr-tv-layout">
          <div className="rr-tv-board-card">
            {showConfetti && (
              <div className="rr-confetti-layer">
                {confettiPieces.map((piece) => (
                  <span
                    key={piece.id}
                    className="rr-confetti"
                    style={{
                      left: piece.left,
                      animationDelay: piece.delay,
                      animationDuration: piece.duration,
                      background: piece.color,
                      transform: `rotate(${piece.rotate})`,
                    }}
                  />
                ))}
              </div>
            )}

            <div className="rr-board-head">
              <div className="rr-chip">Round {state.round}/{state.totalRounds}</div>
              <div className="rr-chip">{state.roundName}</div>
              <div className="rr-chip">{(timeLeftMs / 1000).toFixed(1)}s</div>
            </div>

            <div className="rr-hole-grid">
              {(state.board || []).map((hole) => (
                <div key={hole.holeIndex} className={`rr-hole ${hole.occupied ? 'occupied' : ''}`}>
                  <div className="rr-hole-number">{hole.holeIndex}</div>
                  {hole.occupied ? (
                    <div className={`rr-entity rr-${(hole.entityKind || '').toLowerCase()}`} style={{ '--rr-entity-color': hole.color }}>
                      <span className="rr-entity-symbol">{hole.symbol}</span>
                      <span className="rr-entity-label">{hole.entityLabel}</span>
                    </div>
                  ) : (
                    <div className="rr-hole-empty">Empty</div>
                  )}
                </div>
              ))}
            </div>

            {state.phase === 'round-briefing' && (
              <div className="rr-overlay">
                <h3>{state.roundName}</h3>
                <p>{roundRules[state.round]}</p>
                <p>Starting in {(timeLeftMs / 1000).toFixed(1)}s</p>
              </div>
            )}

            {state.phase === 'round-summary' && (
              <div className="rr-overlay">
                <h3>Round {state.round} Summary</h3>
                <p>Mole wins: {state.roundStats?.moleWins ?? 0}</p>
                <p>Bomb hits: {state.roundStats?.bombHits ?? 0}</p>
                {state.roundStats?.fastestTapPlayer && (
                  <p>Fastest tap: {state.roundStats.fastestTapPlayer} ({state.roundStats.fastestTapMs}ms)</p>
                )}
                <p>Next phase in {(timeLeftMs / 1000).toFixed(1)}s</p>
              </div>
            )}

            {state.phase === 'game-over' && (
              <div className="rr-overlay rr-podium">
                <h3>Final Podium</h3>
                {bottomUpRevealRows.map((entry, index) => (
                  <div key={entry.id} className={`rr-final-score rr-reveal-row ${index < podiumRevealCount ? 'shown' : ''}`}>
                    <span>
                      #{scoreboard.length - index} {entry.avatar} {entry.name}
                    </span>
                    <strong>{entry.points}</strong>
                  </div>
                ))}
                <button className="primary-button" onClick={() => socket.emit('reactionStartGame', { sessionId })}>Play Again</button>
                <button className="ghost-button" onClick={onReturnToPartyPack}>Return to Party Pack</button>
              </div>
            )}
          </div>

          <div className="rr-tv-score-card">
            {leaderboardLocked ? (
              <div className="rr-locked-board">
                <h3>Leaderboard Locked</h3>
                <p>Round 3 scores are hidden until the final podium reveal.</p>
                {(scoreboard || []).map((entry, index) => (
                  <div key={entry.id} className="rr-score-row">
                    <div className="rr-score-left">
                      <span className="rr-rank">?</span>
                      <span className="rr-avatar">{entry.avatar}</span>
                      <span className="rr-name">{entry.name}</span>
                    </div>
                    <div className="rr-score-right">
                      <strong>??</strong>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <h3>Leaderboard</h3>
                {scoreboard.map((entry, index) => (
                  <div key={entry.id} className="rr-score-row">
                    <div className="rr-score-left">
                      <span className="rr-rank">#{index + 1}</span>
                      <span className="rr-avatar">{entry.avatar}</span>
                      <span className="rr-name">{entry.name}</span>
                    </div>
                    <div className="rr-score-right">
                      <strong>{entry.points}</strong>
                    </div>
                  </div>
                ))}
              </>
            )}

            <h4>Live Feed</h4>
            <div className="rr-feed-list">
              {(state.feed || []).slice(-14).reverse().map((event) => (
                <div key={event.id} className="rr-feed-item">
                  {event.type === 'mole-resolved' ? (
                    <span>{event.tie ? 'Tie' : 'Win'}: {event.playerNames?.join(', ') || 'Unknown'} on hole {event.holeIndex}</span>
                  ) : event.type === 'bomb-hit' ? (
                    <span>{event.playerName} hit {event.entityId} on hole {event.holeIndex} ({event.delta})</span>
                  ) : (
                    <span>{event.message || event.type}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
